/**
 * InsightCard — Single contextual insight on Dashboard.
 * Max 1 visible. Dismissible (persisted to Firestore). Clinical tone.
 */

import { useMemo, useState, useEffect } from 'react';
import { doc, updateDoc, arrayUnion } from 'firebase/firestore';
import { db } from '@/firebase/config';
import { useAuth } from '@/contexts/AuthContext';
import { T } from '@/styles/tokens';
import type { VerdictSummary } from '@/types';

interface InsightCardProps {
  verdicts: VerdictSummary[];
  floorHourly: number;
}

interface Insight {
  id: string;
  title: string;
  body: string;
  icon: 'alert' | 'clock';
}

const AlertIcon = (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none"
    stroke={T.primary} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
    <line x1="12" y1="9" x2="12" y2="13" /><line x1="12" y1="17" x2="12.01" y2="17" />
  </svg>
);

const ClockIcon = (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none"
    stroke={T.primary} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10" /><polyline points="12 6 12 12 16 14" />
  </svg>
);

const CloseIcon = (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none"
    stroke={T.textMuted} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
  </svg>
);

export function InsightCard({ verdicts, floorHourly }: InsightCardProps) {
  const { user, profile } = useAuth();
  const [localDismissed, setLocalDismissed] = useState<string[]>([]);

  useEffect(() => {
    if (profile?.dismissedInsights) {
      setLocalDismissed(profile.dismissedInsights as string[]);
    }
  }, [profile?.dismissedInsights]);

  const insight: Insight | null = useMemo(() => {
    if (floorHourly <= 0) return null;

    // Below floor
    const belowCount = verdicts.filter(v =>
      !v.sold && v.effectiveHourly != null && v.effectiveHourly < floorHourly
    ).length;
    if (belowCount >= 1 && !localDismissed.includes('below-floor')) {
      return {
        id: 'below-floor',
        title: `${belowCount} piece${belowCount !== 1 ? 's' : ''} priced below floor`,
        body: 'Effective hourly on these falls below your hourly rate.',
        icon: 'alert',
      };
    }

    // Below min wage
    if (floorHourly < 725 && !localDismissed.includes('below-min-wage')) {
      return {
        id: 'below-min-wage',
        title: 'Your floor rate is below minimum wage',
        body: `Current burn and capacity produce a floor of $${(floorHourly / 100).toFixed(2)}/hr.`,
        icon: 'alert',
      };
    }

    // Stale unsold
    const ninety = Date.now() - 90 * 86400000;
    const staleCount = verdicts.filter(v =>
      !v.sold && new Date(v.createdAt).getTime() < ninety
    ).length;
    if (staleCount >= 3 && !localDismissed.includes('stale-unsold')) {
      return {
        id: 'stale-unsold',
        title: `${staleCount} unsold pieces older than 90 days`,
        body: 'Consider reviewing pricing or marking as sold.',
        icon: 'clock',
      };
    }

    return null;
  }, [verdicts, floorHourly, localDismissed]);

  const handleDismiss = async (id: string) => {
    setLocalDismissed(prev => [...prev, id]);
    if (user) {
      updateDoc(doc(db, 'users', user.uid), {
        dismissedInsights: arrayUnion(id),
      }).catch(console.error);
    }
  };

  if (!insight) return null;

  return (
    <div style={{
      background: T.primarySubtle,
      border: `1px solid rgba(61,171,165,0.08)`,
      borderRadius: T.radiusMd,
      padding: '14px 16px',
      display: 'flex',
      gap: 12,
      alignItems: 'flex-start',
      marginBottom: 12,
    }}>
      <div style={{
        width: 36, height: 36, borderRadius: 18,
        background: T.primaryGlow,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        flexShrink: 0,
      }}>
        {insight.icon === 'alert' ? AlertIcon : ClockIcon}
      </div>
      <div style={{ flex: 1 }}>
        <div style={{ fontSize: 14, fontWeight: 600, color: T.text, fontFamily: T.fontFamily }}>{insight.title}</div>
        <div style={{ fontSize: 12, color: T.textSecondary, marginTop: 2, lineHeight: 1.4 }}>{insight.body}</div>
      </div>
      <button onClick={() => handleDismiss(insight.id)} style={{
        width: 28, height: 28, border: 'none', background: 'none',
        cursor: 'pointer', padding: 0, flexShrink: 0,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}>
        {CloseIcon}
      </button>
    </div>
  );
}
